import moment from 'moment';
import { createContext, useState } from 'react';

import type { PropsWithChildren } from 'react';
import type { GilmoreEvent } from '@/api/inferred.types';

type GilmoreHealthContextValue = {
    gilmoreEventData: any[];
    mostRecent?: null | GilmoreEvent;
    thereIsActiveEvent: boolean;
    gilmoreEventDataSet: (events: GilmoreEvent[]) => void;
};

const GilmoreHealthContext = createContext<GilmoreHealthContextValue>({
    gilmoreEventData: [],
    mostRecent: null,
    thereIsActiveEvent: false,
    gilmoreEventDataSet: () => undefined,
});

const GILMORE_DOMAIN = 'GILMORE';

const getMostRecent = (events: GilmoreEvent[]) => {
    if (!events?.length) {
        return null;
    }
    return events
        .filter((ev) => ev!.service === GILMORE_DOMAIN)
        .sort((a, b) => b!.startTime - a!.startTime)
        .pop();
};

const isThereActiveEvent = (events: GilmoreEvent[]) => {
    if (!events?.length) {
        return false;
    }
    const now = moment().unix();
    return events.some(
        (ev) => ev!.service === GILMORE_DOMAIN && ev!.startTime <= now && now <= ev!.endTime,
    );
};

/**
 * Context provider to hold data related to Gilmore outage events, this should be used close to the
 * root of the app to reduce redundancy
 * @param {object[]} value array of events.
 */
const GilmoreHealthProvider = ({
    children,
    value = [],
}: PropsWithChildren<{ value: GilmoreEvent[] }>) => {
    const [data, dataSet] = useState({
        gilmoreEventData: value,
        mostRecent: getMostRecent(value),
        thereIsActiveEvent: isThereActiveEvent(value),
    });
    const gilmoreEventDataSet = (events: GilmoreEvent[]) => {
        dataSet({
            gilmoreEventData: events,
            mostRecent: getMostRecent(events),
            thereIsActiveEvent: isThereActiveEvent(events),
        });
    };
    const initialValue = {
        ...data,
        gilmoreEventDataSet,
    };
    return (
        <GilmoreHealthContext.Provider value={initialValue}>
            {children}
        </GilmoreHealthContext.Provider>
    );
};

export { GilmoreHealthProvider, GilmoreHealthContext };
